<template>
    <div>
        <v-dialog persistent scrollable v-model="value" max-width="800">
            <v-card class="tour-intro">
                <v-card-title class="headline">Welcome to the BC Water Tool</v-card-title>
                <v-card-text class="body">
                    <p>The BC Water Tool is a modular application which provides access to water related data and knowledge in support of sustainable resource management. On this site you’ll find custom watershed reports for every stream, river and watershed in the Kootenay Boundary Region. You’ll also find monitoring data from more than 50 organizations, at more than 50,000 locations across southern BC.                    </p>
                    <p>This short tour will help you find your way around. Let’s get started!
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" flat @click.native="cancel">No Thanks</v-btn>
                    <v-btn color="primary" @click.native="start">Sure</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <div v-show="showOverlay" class="tour-container">
            <v-tour :name="name" :steps="steps" :callbacks="tourCallbacks">
                <template slot-scope="tour">
                    <transition>
                        <v-step
                            v-if="tour.currentStep === index"
                            v-for="(step, index) of tour.steps"
                            :key="index"
                            :step="step"
                            :previous-step="tour.previousStep"
                            :next-step="tour.nextStep"
                            :stop="tour.stop"
                            :isFirst="tour.isFirst"
                            :isLast="tour.isLast"
                        >
                            <v-layout slot="content" style="color:#444;">
                                <div v-html="step.content"></div>
                            </v-layout>
                            <v-layout slot="actions">
                                <v-card-actions>
                                    <v-btn flat small color="primary" v-if="tour.currentStep > 0" @click="tour.previousStep"><v-icon>chevron_left</v-icon>Previous</v-btn>
                                    <v-btn flat small color="error" @click="tour.stop">Leave Tour<v-icon>exit_to_app</v-icon></v-btn>
                                    <v-btn flat small color="primary" @click="()=>{tour.isLast ? tour.stop() : tour.nextStep()}">Next<v-icon>chevron_right</v-icon></v-btn>
                                </v-card-actions>
                            </v-layout>
                        </v-step>
                    </transition>
                </template>
            </v-tour>
            <v-tour :name="'finalStep'" :steps="finalStep" :callbacks="finalCallbacks">
                <template slot-scope="tour">
                    <transition>
                        <v-step
                            v-if="tour.currentStep === index"
                            v-for="(step, index) of tour.steps"
                            :key="index"
                            :step="step"
                            :previous-step="tour.previousStep"
                            :next-step="tour.nextStep"
                            :stop="tour.stop"
                            :isFirst="tour.isFirst"
                            :isLast="tour.isLast"
                        >
                            <v-layout slot="content" style="color:#444;">
                                <div v-html="step.content"></div>
                            </v-layout>
                            <v-layout slot="actions">
                                <v-card-actions>
                                    <v-btn flat small color="primary" @click.native="tour.stop">OK<v-icon>check</v-icon></v-btn>
                                </v-card-actions>
                            </v-layout>
                        </v-step>
                    </transition>
                </template>
            </v-tour>
            <div v-show="showOverlay" id="tour-highlighter">
                <svg id="overlay" :width="viewWidth" :height="viewHeight" xmlns="http://www.w3.org/2000/svg">
                    <path :d="pathOverlay"></path>
                </svg>
            </div>
        </div>
    </div>
</template>
<script>
import { SHOW_MAP_SIDEBAR, SHOW_NAV_DRAWER, SHOW_MAP_LEGEND } from '@/constants/actions';
import { setTimeout } from 'timers';

export default {
    props: {
        name: {
            type: String,
            default: 'myTour',
        },
        value: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            tourCallbacks: {
                onStart: this.onStartTour,
                onNextStep: this.onNextStep,
                onPreviousStep: this.onPreviousStep,
                onStop: this.onStop,
            },
            finalCallbacks: {
                onStart: this.onFinalStart,
                onStop: this.onFinalStop,
            },
            showOverlay: false,
            // basic overlay, starts counter clockwise to define a 4 sided polygon, and then clockwise to define
            // the "cutout".
            pathOverlay: 'M 0 0 L 0 1  L 1 1 L 1 0 L 0 0 z M 3 3 L 4 3 L 4 4 L 3 4 L 3 3 z',
            viewHeight: 0,
            viewWidth: 0,
            finalStep: [
                {
                    target: '#tourActivator',
                    content: 'To activate this tour again, click on this icon!',
                    params: {
                        placement: 'right',
                        adjustments: {
                            left: -10,
                            width: 20,
                            top: -10,
                            height: 20,
                        },
                    },
                },
            ],
            steps: [
                {
                    target: '#navDrawer',
                    content: 'This is the Navigation Drawer. It lets you change the contents of the map. Click on any of the icons to change the map module and explore functionality in the tool.',
                    params: {
                        placement: 'right',
                        adjustments: {
                            top: 64,
                            height: -64,
                        },
                    },
                },
                {
                    target: '#navigationToggle',
                    content: 'The three horizontal lines at the top open and close the drawer.',
                    params: {
                        placement: 'right',
                        before: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                            if (!document.querySelector('#hack-nav-drawer-0')) {
                                const hacks = [];
                                for (let i = 0; i <= 5; i += 1) {
                                    hacks[i] = document.createElement('div');
                                    hacks[i].classList.add('tourHack');
                                    const referencebbox = document.querySelector(`#nav-drawer-${i}`).getBoundingClientRect();
                                    hacks[i].style.top = `${referencebbox.top}px`;
                                    hacks[i].style.left = `${referencebbox.left}px`;
                                    hacks[i].style.width = `${referencebbox.width}px`;
                                    hacks[i].style.height = `${referencebbox.height}px`;
                                    hacks[i].setAttribute('id', `hack-nav-drawer-${i}`);
                                    document.body.appendChild(hacks[i]);
                                }
                                setTimeout(() => {
                                    for (let i = 0; i <= 5; i += 1) {
                                        const referencebbox = document.querySelector(`#nav-drawer-${i}`).getBoundingClientRect();
                                        hacks[i].style.top = `${referencebbox.top}px`;
                                        hacks[i].style.left = `${referencebbox.left}px`;
                                        hacks[i].style.width = `${referencebbox.width}px`;
                                        hacks[i].style.height = `${referencebbox.height}px`;
                                        hacks[i].setAttribute('id', `hack-nav-drawer-${i}`);
                                        document.body.appendChild(hacks[i]);
                                    }
                                }, 200);
                            }
                        },
                    },
                },
                {
                    target: '#hack-nav-drawer-0',
                    content: 'Markers show locations of existing water rights. Clicking on any stream, lake, or river will generate a custom, watershed based supply and demand report.',
                    params: {
                        placement: 'right',
                        timeout: 100, // wait 100 ms before doing this. Nav drawer takes .5s to expand!
                        before: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                        },
                    },
                },
                {
                    target: '#hack-nav-drawer-1',
                    content: 'Markers show active and historical surface water quantity measurement locations.',
                    params: {
                        placement: 'right',
                        before: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                        },
                    },
                },
                {
                    target: '#hack-nav-drawer-2',
                    content: 'Waterbodies where <strong>surface water quality</strong> has been measured.',
                    params: {
                        placement: 'right',
                        before: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                        },
                    },
                },
                {
                    target: '#hack-nav-drawer-3',
                    content: 'Wells where <strong>groundwater quality</strong> has been measured.',
                    params: {
                        placement: 'right',
                        before: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                        },
                    },
                },
                {
                    target: '#hack-nav-drawer-4',
                    content: 'Wells where <strong>groundwater levels</strong> have been measured.',
                    params: {
                        placement: 'right',
                        before: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                        },
                    },
                },
                {
                    target: '#hack-nav-drawer-5',
                    content: 'Active and historical <strong>weather stations</strong> (temperature, precipitation, and snow).',
                    params: {
                        placement: 'right',
                        before: () => {
                            // in case the user clicks forward and then back.
                            this.$store.dispatch(SHOW_NAV_DRAWER, true);
                            if (!document.querySelector('#hack-nav-drawer-0')) {
                                const hacks = [];
                                for (let i = 0; i <= 5; i += 1) {
                                    hacks[i] = document.createElement('div');
                                    hacks[i].classList.add('tourHack');
                                    const referencebbox = document.querySelector(`#nav-drawer-${i}`).getBoundingClientRect();
                                    hacks[i].style.top = `${referencebbox.top}px`;
                                    hacks[i].style.left = `${referencebbox.left}px`;
                                    hacks[i].style.width = `${referencebbox.width}px`;
                                    hacks[i].style.height = `${referencebbox.height}px`;
                                    hacks[i].setAttribute('id', `hack-nav-drawer-${i}`);
                                    document.body.appendChild(hacks[i]);
                                }
                                setTimeout(() => {
                                    for (let i = 0; i <= 5; i += 1) {
                                        const referencebbox = document.querySelector(`#nav-drawer-${i}`).getBoundingClientRect();
                                        hacks[i].style.top = `${referencebbox.top}px`;
                                        hacks[i].style.left = `${referencebbox.left}px`;
                                        hacks[i].style.width = `${referencebbox.width}px`;
                                        hacks[i].style.height = `${referencebbox.height}px`;
                                        hacks[i].setAttribute('id', `hack-nav-drawer-${i}`);
                                        document.body.appendChild(hacks[i]);
                                    }
                                }, 200);
                            }
                        },
                        after: () => {
                            this.$store.dispatch(SHOW_NAV_DRAWER, false);
                        },
                    },
                },
                {
                    target: '.search-bar',
                    content: 'Search for communities, rivers, lakes, water rights. You can also search by geographic coordinates (lat, long, decimal degrees), or Watershed Feature ID (WFI - you’ll find that unique reference on each report).',
                    params: {
                        placement: 'bottom',
                    },
                },
                {
                    target: '#sidebar-toggle',
                    content: 'Click these three dots to open the context aware sidebar.',
                    params: {
                        placement: 'left',
                    },
                },
                {
                    target: '.map-sidebar',
                    content: 'In the sidebar, you can filter the locations on the map. Filter options change depending on which module you are in.',
                    params: {
                        before: () => {
                            this.$store.dispatch(SHOW_MAP_SIDEBAR, true);
                        },
                        after: () => {
                            this.$store.dispatch(SHOW_MAP_SIDEBAR, false);
                        },
                        placement: 'left',
                    },
                },
                {
                    target: '#map-legend-button',
                    content: 'Show the legend',
                    params: {
                        placement: 'right',
                    },
                },
                {
                    target: '#map-legend-popup',
                    content: 'Gives you information on the various features of the map',
                    params: {
                        placement: 'right',
                        timeout: 100, // wait 100 ms before doing this
                        before: () => {
                            this.$store.dispatch(SHOW_MAP_LEGEND, true);
                        },
                    },
                },
                {
                    target: '#map-style-switcher-button',
                    content: 'Change the map background',
                    params: {
                        placement: 'right',
                    },
                },
                {
                    target: '#map-save-button',
                    content: 'Click the save button to save your current map view and filters (add it to your bookmarks or share with a friend), and download map data.',
                    params: {
                        placement: 'right',
                    },
                },
                {
                    target: '.mapboxgl-ctrl-top-left>.mapboxgl-ctrl',
                    content: 'Zoom in and out (you can also use the scrollwheel of your mouse)',
                    params: {
                        placement: 'right',
                    },
                },
                {
                    target: '.speed-dial',
                    content: 'The orange numbers show the status of your reports generated. Click the icon to download.',
                    params: {
                        placement: 'right',
                        adjustments: {
                            left: -10,
                            width: 20,
                            top: -10,
                            height: 20,
                        },
                    },
                },
            ],
        };
    },
    methods: {
        start() {
            this.$emit('input', false);
            this.$tours[this.name].start();
        },
        cancel() {
            this.$tours.finalStep.start();
            localStorage.setItem('ShowTour', false);
            this.$emit('input', false);
        },
        onFinalStart() {
            this.showOverlay = true;
            this.moveHelperLayer(this.finalStep[0].target, 0, this.finalStep);
        },
        onStartTour() {
            this.showOverlay = true;
            this.moveHelperLayer(this.steps[0].target, 0, this.steps);
        },
        // this is the function which runs when the user clicks "next" on at step <step>
        // so, if the user clicks "next" on step 2, the next step shown will be 3, and this function
        // is called before 3 runs.
        onNextStep(step) {
            // give it a bit of time before we show the highlighter/overlay
            let to = 100;
            if (this.steps[step + 1].params && this.steps[step + 1].params.timeout) {
                if (this.steps[step + 1].params.timeout) {
                    to = this.steps[step + 1].params.timeout;
                }
            }
            setTimeout(() => { this.moveHelperLayer(this.steps[step + 1].target, step + 1, this.steps); }, to);

            if (this.steps[step].params && this.steps[step].params.after) {
                if (typeof this.steps[step].params.after === 'function') {
                    this.steps[step].params.after();
                }
            }
            if (this.steps[step + 1].params && this.steps[step + 1].params.before) {
                if (typeof this.steps[step + 1].params.before === 'function') {
                    this.steps[step + 1].params.before();
                }
            }
        },
        // this is the function which runs when the user clicks "previous" on step <step>
        // similar to onNextStep but this is for clicking on the "previous" button.
        onPreviousStep(step) {
            let to = 100;
            if (this.steps[step - 1].params && this.steps[step - 1].params.timeout) {
                if (this.steps[step - 1].params.timeout) {
                    to = this.steps[step - 1].params.timeout;
                }
            }
            // give it a bit of time before we show the highlighter/overlay
            setTimeout(() => { this.moveHelperLayer(this.steps[step - 1].target, step - 1, this.steps); }, to);

            if (this.steps[step].params && this.steps[step].params.after) {
                if (typeof this.steps[step].params.after === 'function') {
                    this.steps[step].params.after();
                }
            }
            if (this.steps[step - 1].params && this.steps[step - 1].params.before) {
                if (typeof this.steps[step - 1].params.before === 'function') {
                    this.steps[step - 1].params.before();
                }
            }
        },
        onStop() {
            if (!this.$tours.finalStep.isRunning && this.$tours[this.name].isRunning) {
                this.$tours.finalStep.start();
            } else if (this.$tours.finalStep.isRunning) {
                // finalStep is running, just exit, user might be angry and clicking esc furiously
                this.$tours.finalStep.currentStep = -1;
            }
        },
        onFinalStop() {
            this.showOverlay = false;
            localStorage.setItem('ShowTour', false);
        },
        // make an overlay with a "cutout" in svg
        // you'll probably want to look at generateOverlayPath instead of this.
        moveHelperLayer(targetSelector, step, stepGroup) {
            const target = document.querySelector(targetSelector);
            const boundingRect = target.getBoundingClientRect();
            if (boundingRect.width > 0) {
                const dims = {
                    left: boundingRect.left,
                    top: boundingRect.top,
                    width: boundingRect.width,
                    height: boundingRect.height };
                // if there are adjustments needed as per the steps defined, apply
                // them to the boundingbox for the "highlight" cutout of the path.
                // you can use this sort of thing to give yourself some padding if the
                // selector can't manage to highlight the full element.
                if (stepGroup[step].params && stepGroup[step].params.adjustments) {
                    Object.keys(stepGroup[step].params.adjustments).forEach((key) => {
                        dims[key] += stepGroup[step].params.adjustments[key];
                    });
                }
                this.generateOverlayPath(dims);
            }
        },
        // generates the svg "overlay" with a cutout as defined as boundingRect
        generateOverlayPath(boundingRect) {
            this.viewWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
            this.viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
            this.pathOverlay = `M 0 0 L 0 ${this.viewHeight} L ${this.viewWidth} ${this.viewHeight} L ${this.viewWidth} 0 L 0 0 z`;
            // move to the top left point of the cutout
            this.pathOverlay += ` M ${boundingRect.left} ${boundingRect.top}`;
            // draw a Line to the top right point of the cutout
            this.pathOverlay += ` L ${boundingRect.left + boundingRect.width} ${boundingRect.top}`;
            // draw a Line to the bottom right point of the cutout
            this.pathOverlay += ` L ${boundingRect.left + boundingRect.width} ${boundingRect.top + boundingRect.height}`;
            // draw a Line to the bottom left point of the cutout
            this.pathOverlay += ` L ${boundingRect.left} ${boundingRect.top + boundingRect.height}`;
            // draw a Line to the top left point of the cutout, end path
            this.pathOverlay += ` L ${boundingRect.left} ${boundingRect.top} z`;
        },
    },
    mounted() {
        this.showOverlay = false;
    },
};
</script>
<style lang="scss">
    .v-tour {
        .v-step {
            background:white;
            z-index:35;
        }
    }
    .tour-container {
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        z-index:30;
        transition: all .2s ease-out;
    }
    #tour-highlighter {
        box-sizing: content-box;
        position: fixed;
        transition: all .2s ease-out;
        z-index: 31;
    }
    #overlay path{
        transition: all .2s ease-out;
        opacity:1;
        fill:#000000;
        fill-opacity:0.6;
        stroke: #FFFFFF;
        stroke-width:1;
        stroke-opacity:1;
    }
    .tour-intro {
        width: 100% !important; // IE11

        .body {
            font-size: 0.9rem;
        }
    }
    // hack for proper positioning of vue-tour popper.js elements.
    .tourHack {
        position: fixed;
        top:0;
        left:0;
        width:0;
        height:0;
        z-index:-1;
    }
</style>



// WEBPACK FOOTER //
// src/components/Tour.vue